import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import {
  AdminButton,
  AdminCard,
  AdminCardHeader,
  AdminSkeleton,
  AdminStatePanel,
  AdminStatusPill,
} from './AdminUi'
import '../styles/admin-diagnostics.css'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
}

type DiagnosticState = 'ok' | 'warning' | 'error'
type DiagnosticCheck = {
  id: string
  label: string
  state: DiagnosticState
  summary: string
  detail: string
  verification: 'live-probe' | 'configuration'
  durationMs?: number
}

type DiagnosticsPayload = {
  generatedAt: string
  environment: string
  overall: DiagnosticState
  checks: DiagnosticCheck[]
  operations: {
    failedTransactionalEmail: number | null
    pendingTransactionalEmail: number | null
    failedAdminActions24h: number | null
  }
  safety: {
    readOnly: boolean
    secretsExposed: boolean
    externalProviderCalls: boolean
    contentMediaMutated: boolean
  }
}

const NZ_TIME = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
})

function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}

function tone(state: DiagnosticState): 'success' | 'warning' | 'danger' {
  if (state === 'ok') return 'success'
  if (state === 'warning') return 'warning'
  return 'danger'
}

function stateLabel(state: DiagnosticState) {
  if (state === 'ok') return 'Healthy'
  if (state === 'warning') return 'Attention'
  return 'Problem'
}

function overallHeading(state: DiagnosticState) {
  if (state === 'ok') return 'Core checks look healthy.'
  if (state === 'warning') return 'Diagnostics found something to review.'
  return 'Diagnostics found a system problem.'
}

function formatCheckedAt(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Unknown' : NZ_TIME.format(date)
}

function metricValue(value: number | null) {
  return value === null ? 'Unavailable' : value.toLocaleString('en-NZ')
}

export default function AdminDiagnosticsPage() {
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [diagnostics, setDiagnostics] = useState<DiagnosticsPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [error, setError] = useState('')
  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])

  async function fetchDiagnostics() {
    const response = await fetch('/api/admin/diagnostics', {
      credentials: 'same-origin',
      cache: 'no-store',
    })
    if (response.status === 401) {
      window.location.replace('/admin/login?next=/admin/diagnostics')
      return null
    }
    const payload = await response.json().catch(() => null) as (DiagnosticsPayload & { message?: string }) | null
    if (!response.ok || !payload) throw new Error(payload?.message || 'Diagnostics could not be loaded.')
    return payload
  }

  async function loadInitial() {
    setLoading(true)
    setError('')
    try {
      const sessionResponse = await fetch('/api/admin/session', { credentials: 'same-origin', cache: 'no-store' })
      if (sessionResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/diagnostics')
        return
      }
      if (!sessionResponse.ok) throw new Error('Boss Zone session could not be loaded.')
      const sessionPayload = await sessionResponse.json() as SessionPayload
      const diagnosticPayload = await fetchDiagnostics()
      if (!diagnosticPayload) return
      setSession(sessionPayload)
      setDiagnostics(diagnosticPayload)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Diagnostics could not be loaded.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadInitial()
  }, [])

  async function refresh() {
    if (refreshing) return
    setRefreshing(true)
    setError('')
    try {
      const payload = await fetchDiagnostics()
      if (payload) setDiagnostics(payload)
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : 'Diagnostics could not be refreshed.')
    } finally {
      setRefreshing(false)
    }
  }

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    setError('')
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
      })
      if (!response.ok) throw new Error('logout failed')
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed. Please try again.')
      setLogoutBusy(false)
    }
  }

  if (error && !session && !diagnostics && !loading) {
    return (
      <main className="admin-shell-gate-error">
        <AdminStatePanel
          state="error"
          title="Diagnostics unavailable"
          description={error}
          action={<AdminButton type="button" onClick={() => void loadInitial()}>Retry</AdminButton>}
        />
      </main>
    )
  }

  if (!session || !diagnostics) {
    return (
      <main className="admin-shell-gate-loading" aria-label="Loading Boss Zone Diagnostics">
        <AdminCard><AdminSkeleton lines={8} /></AdminCard>
      </main>
    )
  }

  return (
    <AdminShell
      title="Diagnostics"
      eyebrow="SYSTEM"
      description="Read-only operational checks for the current Sugar Gremlin environment."
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={environmentLabel}
      actions={(
        <AdminButton variant="secondary" type="button" disabled={refreshing} onClick={() => void refresh()}>
          {refreshing ? 'Checking…' : 'Run checks'}
        </AdminButton>
      )}
    >
      {error && <div className="admin-diagnostics__inline-error" role="alert">{error}</div>}

      <section className={`admin-diagnostics__summary is-${diagnostics.overall}`} aria-labelledby="diagnostics-summary-title">
        <div>
          <span className="admin-diagnostics__summary-kicker">SYSTEM HEALTH</span>
          <h2 id="diagnostics-summary-title">{overallHeading(diagnostics.overall)}</h2>
          <p>Checked {formatCheckedAt(diagnostics.generatedAt)} · {diagnostics.environment}</p>
        </div>
        <AdminStatusPill tone={tone(diagnostics.overall)}>{stateLabel(diagnostics.overall)}</AdminStatusPill>
      </section>

      <div className="admin-diagnostics__notice" role="note">
        <strong>Read-only diagnostics</strong>
        <span>Checks can inspect safe health signals, but cannot change settings, run repairs, reveal secrets, edit Content & Media, or send payment/email test traffic.</span>
      </div>

      <section className="admin-diagnostics__checks" aria-label="System checks">
        {diagnostics.checks.map((check) => (
          <AdminCard className="admin-diagnostics__check" key={check.id}>
            <div className="admin-diagnostics__check-topline">
              <span className={`admin-diagnostics__signal is-${check.state}`} aria-hidden="true" />
              <AdminStatusPill tone={tone(check.state)}>{stateLabel(check.state)}</AdminStatusPill>
            </div>
            <h2>{check.label}</h2>
            <strong>{check.summary}</strong>
            <p>{check.detail}</p>
            <div className="admin-diagnostics__check-meta">
              <span>{check.verification === 'live-probe' ? 'Live read-only probe' : 'Configuration check'}</span>
              {typeof check.durationMs === 'number' && <span>{check.durationMs.toFixed(1)} ms</span>}
            </div>
          </AdminCard>
        ))}
      </section>

      <div className="admin-diagnostics__lower-grid">
        <AdminCard>
          <AdminCardHeader
            title="Operational signals"
            description="Sanitized counts only. Customer addresses, email addresses, provider errors and secret values are not shown here."
          />
          <dl className="admin-diagnostics__metric-list">
            <div><dt>Failed transactional email events</dt><dd>{metricValue(diagnostics.operations.failedTransactionalEmail)}</dd></div>
            <div><dt>Pending transactional email events</dt><dd>{metricValue(diagnostics.operations.pendingTransactionalEmail)}</dd></div>
            <div><dt>Failed Admin actions · last 24h</dt><dd>{metricValue(diagnostics.operations.failedAdminActions24h)}</dd></div>
          </dl>
        </AdminCard>
      </div>
    </AdminShell>
  )
}
